import React, { useState, useEffect, useMemo } from 'react';
import { I18n } from 'aws-amplify';
import { Container, Row, Col, Table, Spinner } from 'react-bootstrap';
import { SortDown, SortUp } from 'react-bootstrap-icons';

import { fetchMyUsers } from './api/MyUsers';
import { getMyLicences } from './api/MyLicences';
import { LicenceFilter } from './LicenceFilter';
import { LicenceRecord } from './LicenceRecord';
import { LICENCE, SORT_DIRECTION } from './../../shared/constants/index'

// 1回の「表示単位」として欲しい件数
const PAGE_SIZE = 500;

export default function LicenceList() {
  const [loading, setLoading] = useState(false);

  // 自分が owner のユーザ一覧
  const [users, setUsers] = useState([]);

  // 取得したライセンス一覧（生データ）
  const [licences, setLicences] = useState([]);
  const [nextToken, setNextToken] = useState(null);

  // 「さらに読み込む」ボタンの表示可否
  const [nextRead, setNextRead] = useState(false);

  // LicenceFilter から渡ってくる検索条件
  const [filter, setFilter] = useState({ [LICENCE.TABLE_COLUMN.ACTIVE]: true });

  // ソート状態
  const [licenceSort, setLicenceSort] = useState({
    key: LICENCE.TABLE_COLUMN.UPDATED_AT,
    direction: SORT_DIRECTION.DESC,
  });

  // id → user オブジェクト の Map
  const userMap = useMemo(() => {
    const m = new Map();
    users.forEach((u) => m.set(u.id, u));
    return m;
  }, [users]);

  // 自分の User.id の配列
  const myUserIds = useMemo(() => users.map((u) => u.id), [users]);

  // 1) 初回：自分が owner のユーザ一覧を取得
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const us = await fetchMyUsers(true, 200);
        setUsers(us);
      } catch (e) {
        console.error('fetchMyUsers error:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /**
   * 内部ヘルパー:
   * - listLicences を複数ページ叩いて、少なくとも minCount 件たまるまで items を貯める
   * - startToken から開始し、最後に残った nextToken を返す
   */
  const fetchLicencesUntil = async (startToken, minCount) => {
    let items = [];
    let localNextToken = startToken;
    let first = true;

    while ((first || localNextToken) && items.length < minCount) {
      first = false;

      const res = await getMyLicences(
        {
          myUserIds,
          ...filter,
        },
        localNextToken,
        PAGE_SIZE // 1回あたりの back-end limit
      );

      const pageItems = res.items ?? [];
      items = items.concat(pageItems);
      localNextToken = res.nextToken ?? null;

      if (!localNextToken) {
        break; // これ以上ページが無い
      }
    }

    return { items, nextToken: localNextToken };
  };

  // 2) ライセンスを最初から読み込み（最低 PAGE_SIZE 件たまるまでループ）
  const loadFirst = async () => {
    if (myUserIds.length === 0) {
      setLicences([]);
      setNextToken(null);
      setNextRead(false);
      return;
    }

    setLoading(true);
    try {
      const { items, nextToken: newNextToken } = await fetchLicencesUntil(
        null,
        PAGE_SIZE
      );

      setLicences(items);
      setNextToken(newNextToken);
      setNextRead(!!newNextToken); // まだ nextToken があれば「さらに読み込む」表示
    } catch (e) {
      console.error('getMyLicences(loadFirst) error:', e);
      setLicences([]);
      setNextToken(null);
      setNextRead(false);
    } finally {
      setLoading(false);
    }
  };

  // 3) 追加読み込み（今の nextToken から、最低 PAGE_SIZE 件分を追加で貯める）
  const loadMore = async () => {
    if (!nextToken || loading) return;

    setLoading(true);
    try {
      const { items: addItems, nextToken: newNextToken } = await fetchLicencesUntil(
        nextToken,
        PAGE_SIZE
      );

      setLicences((prev) => [...prev, ...addItems]);
      setNextToken(newNextToken);
      setNextRead(!!newNextToken);
    } catch (e) {
      console.error('getMyLicences(loadMore) error:', e);
    } finally {
      setLoading(false);
    }
  };

  // ソート切替（再フェッチしない）
  const changeLicenceSort = (key, direction) => {
    setLicenceSort({ key, direction });
  };

  // ソートアイコンを返す
  const resSortIcon = (argKey) => {
    if (licenceSort.key === argKey) {
      return licenceSort.direction === SORT_DIRECTION.ASC ? (
        <SortUp
          style={{ cursor: 'pointer' }}
          size={12}
          onClick={() => changeLicenceSort(argKey, SORT_DIRECTION.DESC)}
        />
      ) : (
        <SortDown
          style={{ cursor: 'pointer' }}
          size={12}
          onClick={() => changeLicenceSort(argKey, SORT_DIRECTION.ASC)}
        />
      );
    }
    return (
      <SortDown
        style={{ cursor: 'pointer' }}
        color="gray"
        size={12}
        onClick={() => changeLicenceSort(argKey, SORT_DIRECTION.DESC)}
      />
    );
  };

  // 4) 画面表示用ソート
  const view = useMemo(() => {
    const arr = [...licences];
    const { key, direction } = licenceSort;
    const mul = direction === SORT_DIRECTION.ASC ? 1 : -1;

    const toTs = (v) => (v ? new Date(v).getTime() : 0);

    arr.sort((a, b) => {
      let av;
      let bv;

      switch (key) {
        case LICENCE.TABLE_COLUMN.LICENCE_KEY:
          av = a.licenceKey || '';
          bv = b.licenceKey || '';
          return av.localeCompare(bv) * mul;

        case LICENCE.TABLE_COLUMN.UUID:
          av = a.uuid || '';
          bv = b.uuid || '';
          return av.localeCompare(bv) * mul;

        case LICENCE.TABLE_COLUMN.PERIOD:
          av = a.period || '';
          bv = b.period || '';
          return av.localeCompare(bv) * mul;

        case LICENCE.TABLE_COLUMN.SUBSCRIPTION_EXPIRY_DATE_AT:
          av = a.subscriptionExpiryDateAt || '';
          bv = b.subscriptionExpiryDateAt || '';
          return av.localeCompare(bv) * mul;

        case LICENCE.TABLE_COLUMN.MAINTENANCE_PERIOD:
          av = a.maintenancePeriod || '';
          bv = b.maintenancePeriod || '';
          return av.localeCompare(bv) * mul;

          
        case LICENCE.TABLE_COLUMN.USERNAME: {
          const ua = userMap.get(a.userId);
          const ub = userMap.get(b.userId);
          av = (ua && ua.username) || '';
          bv = (ub && ub.username) || '';

          const nameComp = av.localeCompare(bv);
          if (nameComp !== 0) {
            // ユーザ名でソート
            return nameComp * mul;
          }

          // ユーザ名が同じ場合は updatedAt を第二キーに
          const ta = toTs(a.updatedAt);
          const tb = toTs(b.updatedAt);
          if (ta === tb) return 0;
          return (ta - tb) * mul;
        }

        case LICENCE.TABLE_COLUMN.CREATED_AT: {
          const ca = toTs(a.createdAt);
          const cb = toTs(b.createdAt);
          if (ca === cb) return 0;
          return (ca - cb) * mul;
        }

        case LICENCE.TABLE_COLUMN.UPDATED_AT:
        default: {
          const ua = toTs(a.updatedAt);
          const ub = toTs(b.updatedAt);
          if (ua === ub) {
            const ca = toTs(a.createdAt);
            const cb = toTs(b.createdAt);
            return (ca - cb) * mul;
          }
          return (ua - ub) * mul;
        }
      }
    });

    return arr;
  }, [licences, licenceSort, userMap]);

  // 自動で最初の読み込み（ユーザ一覧 or フィルタが変わったタイミング）
  useEffect(() => {
    if (myUserIds.length > 0) {
      loadFirst();
    } else {
      setLicences([]);
      setNextToken(null);
      setNextRead(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myUserIds.join(','), JSON.stringify(filter)]);

  const noData = !Array.isArray(licences) || licences.length === 0;


  return (
    <>
      <Container className="main-content">
        <Row>
          <Col xs={8} md={8}>
            <h2>{I18n.get('title_licence_list')}</h2>
          </Col>
          <Col xs={4} md={4}></Col>
        </Row>

        {/* 検索フィルタ */}
        <LicenceFilter
          filter={setFilter}
          nextToken={setNextToken}
          nextRead={setNextRead}
          load={loading}
        />

        <Row>
          <Col xs={12} md={12} className="mt-3">

          {loading ? (
            <div className="text-center">
              <Spinner
                as="span"
                animation="border"
                role="status"
                aria-hidden="true"
              />
            </div>
          ) : noData ? (
            // データなし
            <>{I18n.get('general_no_data')}</>
          ):(
            <Table striped bordered hover responsive className="text-nowrap">
              <thead>
                <tr>
                  <th></th>
                  <th>
                    {I18n.get('table_column_licence_licence_key')}{' '}
                    {resSortIcon(LICENCE.TABLE_COLUMN.LICENCE_KEY)}
                  </th>
                  <th>
                    {I18n.get('table_column_licence_uuid')}{' '}
                    {resSortIcon(LICENCE.TABLE_COLUMN.UUID)}
                  </th>
                  <th>
                    {I18n.get('table_column_licence_period')}{' '}
                    {resSortIcon(LICENCE.TABLE_COLUMN.PERIOD)}
                  </th>
                  <th>
                    {I18n.get(
                      'table_column_licence_subscription_expiry_date_at'
                    )}{' '}
                    {resSortIcon(
                      LICENCE.TABLE_COLUMN.SUBSCRIPTION_EXPIRY_DATE_AT
                    )}
                  </th>
                  <th>
                    {I18n.get('table_column_licence_maintenance_period')}{' '}
                    {resSortIcon(LICENCE.TABLE_COLUMN.MAINTENANCE_PERIOD)}
                  </th>
                  <th>
                    {I18n.get('table_column_licence_username')}{' '}
                    {resSortIcon(LICENCE.TABLE_COLUMN.USERNAME)}
                  </th>
                  <th>
                    {I18n.get('general_latest_update')}{' '}
                    {resSortIcon(LICENCE.TABLE_COLUMN.UPDATED_AT)}
                  </th>
                  <th>
                    {I18n.get('general_create_date')}{' '}
                    {resSortIcon(LICENCE.TABLE_COLUMN.CREATED_AT)}
                  </th>
                </tr>
              </thead>

              <tbody>
                {view.map((cLicence, index) => {
                  const u = userMap.get(cLicence.userId);
                  return (
                    <LicenceRecord
                      key={cLicence.id ?? `lr-${index}`}
                      licence={cLicence}
                      user={u}
                      index={index}
                    />
                  );
                })}

                {!loading && view.length === 0 && (
                  <tr>
                    <td colSpan={9} className="text-muted">
                      データがありません
                    </td>
                  </tr>
                )}
              </tbody>

              {
                nextToken ? (
                  <tfoot>
                    <tr>
                    { nextRead ? (
                      <td colSpan={9} className="text-center">
                          <Spinner
                            as="span"
                            animation="border"
                            role="status"
                            aria-hidden="true"
                          />
                      </td>
                    ) : (
                      <td colSpan={9} className="text-center styleLiceRecd"
                        onClick={() => loadMore()} >
                        {I18n.get('general_paging')}
                      </td>
                    )}
                  </tr>
                </tfoot>
            ) : (<></>)
            }
          </Table>
          )}
        </Col>
        </Row>
      </Container>
    </>
  );
}
